import { InnerPageActions } from "@/components/site/InnerPageActions";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Eyebrow } from "@/components/site/Eyebrow";
import { cars, getCar } from "@/lib/cars";
import { breadcrumbScript, faqScript, type FaqItem } from "@/lib/schema";

const durationKeys = [
  { key: "h24", label: "24 Hours" },
  { key: "h48", label: "48 Hours" },
  { key: "d36", label: "3 - 6 Days" },
  { key: "d7", label: "7+ Days" },
] as const;

const ratesFaqs: FaqItem[] = [
  {
    question: "How much is the security deposit?",
    answer:
      "The refundable security deposit is $2,000 on every vehicle in the Avolus fleet, from the BMW X1 through to the Range Rover P530 LWB, Cadillac Escalade and Mercedes S580. It's held on a credit card at the time of delivery and released back to the same card after the vehicle is returned in the condition it went out.",
  },
  {
    question: "What is LDW and is it required?",
    answer:
      "LDW (Loss Damage Waiver) is mandatory at $60 per day on every vehicle. It limits your financial responsibility for physical damage to the rental car, subject to the terms and deductible in your rental agreement.",
  },
  {
    question: "How many kilometres are included per day?",
    answer:
      "Every rental includes 150 km per day. Extra kilometres are $1.75/km on the Range Rover P530 LWB and Cadillac Escalade. Check each vehicle's rate card for its exact extra-kilometre rate. On a 7-day rental that's 1,050 km included.",
  },
  {
    question: "Do rates drop for longer rentals?",
    answer:
      "Yes, significantly. Every vehicle has four tiers: 24-hour, 48-hour, 3-6 day, and 7-plus-day. For example the BMW X1 drops from $200/day to $75/day at seven days, the Range Rover P530 LWB drops from $395/day to $250/day, and the Cadillac Escalade drops from $395/day to $225/day. Longer than seven days, contact us for a custom quote.",
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "The rental balance can be paid by all major credit cards (Visa, Mastercard, and American Express), PayPal, Interac e-Transfer for Canadian customers, cryptocurrency such as Bitcoin and Ethereum, or cash. The refundable $2,000 security deposit must be held on a credit card in the primary driver's name; debit cards and prepaid cards are not accepted for the deposit.",
  },
];

export const Route = createFileRoute("/rates")({
  component: Rates,
  head: () => ({
    meta: [
      { title: "Luxury Car Rental Rates Vancouver | Full Price List | Avolus" },
      { name: "description", content: "Compare Range Rover, BMW, Mercedes, Porsche and Cadillac rental rates in Vancouver. See 24-hour through 7+ day pricing, deposits and mileage." },
      { property: "og:title", content: "Luxury Car Rental Rates Vancouver | Avolus" },
      { property: "og:description", content: "Every rate, every duration, every vehicle: transparent pricing from Avolus in Vancouver." },
      { property: "og:url", content: "https://avoluscars.com/rates" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/rates" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Rates", url: "https://avoluscars.com/rates" },
      ]),
      faqScript(ratesFaqs),
    ],
  }),
});

// Parse "$395/day" -> 395; used only for example totals shown alongside line items.
const dailyNum = (rateStr: string) => {
  const m = rateStr.match(/\$([\d,]+(?:\.\d+)?)/);
  return m ? Number(m[1].replace(/,/g, "")) : 0;
};

function Example({
  title,
  slug,
  days,
  tierKey,
  tierLabel,
}: {
  title: string;
  slug: string;
  days: number;
  tierKey: "h24" | "h48" | "d36" | "d7";
  tierLabel: string;
}) {
  const car = getCar(slug);
  if (!car) return null;
  const perDay = dailyNum(car.rates[tierKey]);
  const rentalSub = perDay * days;
  const ldwPerDay = dailyNum(car.rates.ldw);
  const ldwSub = ldwPerDay * days;
  const total = rentalSub + ldwSub;
  return (
    <div className="border border-border p-6">
      <h3 className="font-display text-xl text-foreground">{title}</h3>
      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <Link to="/fleet/$slug" params={{ slug: car.slug }} className="hover:underline">
          {car.name}
        </Link>{" "}
        · {days} day{days > 1 ? "s" : ""}
      </p>
      <dl className="mt-4 space-y-2 text-sm">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">
            {tierLabel} rate · ${perDay}/day × {days}
          </dt>
          <dd className="text-foreground">${rentalSub.toLocaleString()}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">
            LDW · ${ldwPerDay}/day × {days}
          </dt>
          <dd className="text-foreground">${ldwSub.toLocaleString()}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">150 km/day included ({days * 150} km)</dt>
          <dd className="text-foreground">Included</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Delivery in Vancouver / YVR</dt>
          <dd className="text-foreground">Included</dd>
        </div>
        <div className="flex justify-between border-t border-border pt-3 mt-3">
          <dt className="text-foreground">Subtotal before tax</dt>
          <dd className="text-foreground font-medium">${total.toLocaleString()}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">GST + BC PVRT</dt>
          <dd className="text-muted-foreground">Applied at checkout</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Refundable security deposit</dt>
          <dd className="text-muted-foreground">$2,000 (held, not charged)</dd>
        </div>
      </dl>
    </div>
  );
}

function Rates() {
  return (
    <Layout inner>
      <section className="pt-40 pb-12">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div>
            <Eyebrow>Rates</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground">
              Luxury Car Rental Rates in Vancouver.
            </h1>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div>
            <Eyebrow>Full rate card</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">
              Every vehicle, every duration
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl">
              All rates are quoted per day in Canadian dollars, before GST and BC Passenger
              Vehicle Rental Tax. Extra-kilometre and LDW rates are per vehicle.
            </p>
            <div className="mt-8 border-y border-border md:hidden">
              <div className="grid grid-cols-4 gap-2 border-b border-border py-3 text-center text-xs text-muted-foreground">
                {durationKeys.map((duration) => <span key={duration.key}>{duration.label}</span>)}
              </div>
              {cars.map((car) => (
                <div key={car.slug} className="border-b border-border last:border-b-0 py-4">
                  <Link to="/fleet/$slug" params={{ slug: car.slug }} className="font-display text-xl text-foreground hover:underline underline-offset-4">{car.shortName}</Link>
                  <dl className="mt-3 grid grid-cols-4 gap-2 text-center">
                    {durationKeys.map((duration) => <div key={duration.key} className="min-w-0"><dt className="sr-only">{duration.label}</dt><dd className="text-xs font-medium text-foreground">{car.rates[duration.key]}</dd></div>)}
                  </dl>
                </div>
              ))}
            </div>
            <div className="hidden md:block mt-8 border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-card text-left">
                    <th className="px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-foreground/80 font-medium">Vehicle</th>
                    {durationKeys.map((d) => (
                      <th key={d.key} className="px-5 py-4 text-[10px] uppercase tracking-[0.25em] text-foreground/80 font-medium">
                        {d.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cars.map((c, i) => (
                    <tr key={c.slug} className={`border-b border-border last:border-b-0 ${i % 2 === 1 ? "bg-card/40" : ""}`}>
                      <td className="px-5 py-5">
                        <Link to="/fleet/$slug" params={{ slug: c.slug }} className="font-display text-base text-foreground hover:underline underline-offset-4">
                          {c.name}
                        </Link>
                      </td>
                      {durationKeys.map((d) => (
                        <td key={d.key} className="px-5 py-5 text-muted-foreground">{c.rates[d.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <InnerPageActions />
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div>
            <Eyebrow>What's in the price</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">
              What's included vs. what's extra
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              <div className="border border-border p-6">
                <h3 className="font-display text-xl text-foreground">Included with every rental</h3>
                <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                  <li><span className="text-foreground">150 km per day</span> of mileage allowance.</li>
                  <li><span className="text-foreground">$5,000,000 third-party liability</span> insurance.</li>
                  <li><span className="text-foreground">Complimentary delivery and collection</span> within Vancouver and to YVR; elsewhere quoted.</li>
                  <li><span className="text-foreground">Unbranded vehicle</span>: no wraps, decals, or rental markers.</li>
                </ul>
              </div>
              <div className="border border-border p-6">
                <h3 className="font-display text-xl text-foreground">Not included, billed separately</h3>
                <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                   <li><span className="text-foreground">Extra kilometres:</span> $1.75/km on the Escalade, X7 and S580; $1.50/km on the Macan, M3, M440i and X1.</li>
                   <li><span className="text-foreground">Range Rover extra kilometres:</span> $1.75/km over 150 km per day.</li>
                  <li><span className="text-foreground">Loss Damage Waiver (LDW):</span>&nbsp;mandatory $60/day on every vehicle.</li>
                  <li><span className="text-foreground">Refundable security deposit:</span> $2,000 on every vehicle, held on a credit card at delivery.</li>
                  <li><span className="text-foreground">Fuel:</span> return with the same fuel level; anything lower is billed at cost plus a refuelling fee.</li>
                  <li><span className="text-foreground">Delivery outside Vancouver / YVR:</span> Squamish, Whistler, Abbotsford and beyond are quoted per trip.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div>
            <Eyebrow>Taxes and fees</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">
              Taxes and fees explained
            </h2>
            <div className="mt-6 space-y-4 text-muted-foreground">
              <p>
                All rates on this page are shown before tax. Two government taxes apply to
                short-term passenger vehicle rentals in British Columbia:
              </p>
              <ul className="space-y-3 list-disc pl-5">
                <li>
                  <span className="text-foreground">Federal GST</span>: the standard federal
                  Goods and Services Tax, applied to the rental subtotal.
                </li>
                <li>
                  <span className="text-foreground">BC Passenger Vehicle Rental Tax (PVRT)</span>:
                  a per-day provincial tax that BC applies specifically to short-term passenger
                  vehicle rentals in addition to PST.
                </li>
              </ul>
              <p>
                Both are calculated automatically at checkout and shown as separate line items on
                your rental agreement, so the final number is never a surprise. If you need the
                exact tax breakdown for a specific vehicle and date range before booking, contact
                us and we'll send a written quote.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div>
            <Eyebrow>Example costs</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">
              Three worked examples
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl">
              Line-item breakdowns using real published rates. Taxes are applied on top of the
              subtotal; the security deposit is held on a credit card, not charged.
            </p>
            <div className="mt-8 grid gap-6 lg:grid-cols-3">
              <Example
                title="A weekend in the BMW X1"
                slug="bmw-x1-2023"
                days={2}
                tierKey="h48"
                tierLabel="48-hour"
              />
              <Example
                title="A week in the Cadillac Escalade"
                slug="cadillac-escalade-2023"
                days={7}
                tierKey="d7"
                tierLabel="7-day"
              />
              <Example
                title="A wedding day in the Mercedes S580"
                slug="mercedes-s-class-2022"
                days={1}
                tierKey="h24"
                tierLabel="24-hour"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div>
            <Eyebrow>Frequently asked questions</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mt-2">About our rates</h2>
            <div className="mt-8 divide-y divide-border border-y border-border">
              {ratesFaqs.map((f) => (
                <details key={f.question} className="group py-5">
                  <summary className="cursor-pointer list-none text-foreground flex justify-between items-center">
                    <span>{f.question}</span>
                    <span className="ml-4 text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-muted-foreground">{f.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
}
